export const DEFAULT_INSURANCE_MATERIAL_TYPE = "rawMaterial";

export const MIN_INSURANCE_PREMIUM_USD = 20;

export const INSURANCE_MATERIAL_UPDATED_EVENT =
  "insurance-material-updated";

export const INSURANCE_MATERIAL_RULES = {
  pipesAndAccessories: {
    value: "pipesAndAccessories",
    label: "Tubería y accesorios",
    factor: 1.2,
    ratePercent: 0.24,
  },
  rawMaterial: {
    value: "rawMaterial",
    label: "Materia Prima",
    factor: 1.2,
    ratePercent: 0.22,
  },
  sparePartsTools: {
    value: "sparePartsTools",
    label: "Repuestos y herramientas (Producción, Mantenimiento, Calidad y Proyectos)",
    factor: 1.2,
    ratePercent: 0.24,
  },
  machineryInvestment: {
    value: "machineryInvestment",
    label: "Equipos y maquinaria (inversión)",
    factor: 1.25,
    ratePercent: 0.24,
  },
  machineryForSale: {
    value: "machineryForSale",
    label: "Equipos y maquinarias (incluyendo repuestos) para la venta",
    factor: 1.25,
    ratePercent: 0.24,
  },
};

const INSURANCE_MATERIAL_ALIASES = {
  accessories: "pipesAndAccessories",
  machinery: "machineryInvestment",
};

function normalizeInsuranceMaterialText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,;:/_\-()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

export const INSURANCE_MATERIAL_OPTIONS = Object.values(
  INSURANCE_MATERIAL_RULES,
).map((rule) => ({
  value: rule.value,
  label: `${rule.label} - Factor ${rule.factor.toFixed(
    2,
  )} - Tasa ${rule.ratePercent}%`,
}));

export function getInsuranceRule(materialType) {
  const ruleKey = INSURANCE_MATERIAL_ALIASES[materialType] || materialType;

  return (
    INSURANCE_MATERIAL_RULES[ruleKey] ||
    INSURANCE_MATERIAL_RULES[DEFAULT_INSURANCE_MATERIAL_TYPE]
  );
}

export function getInsuranceMaterialTypeByImportType(importTypeName) {
  const normalized = normalizeInsuranceMaterialText(importTypeName);

  if (!normalized) return DEFAULT_INSURANCE_MATERIAL_TYPE;

  if (normalized.includes("MATERIA PRIMA")) {
    return "rawMaterial";
  }

  if (
    normalized.includes("TUBERIA") &&
    normalized.includes("ACCESORIOS")
  ) {
    return "pipesAndAccessories";
  }

  if (
    normalized.includes("REPUESTOS") &&
    normalized.includes("HERRAMIENTAS")
  ) {
    return "sparePartsTools";
  }

  if (
    normalized.includes("VENTA") &&
    normalized.includes("EQUIPOS") &&
    normalized.includes("MAQUINARIAS")
  ) {
    return "machineryForSale";
  }

  if (
    normalized.includes("INVERSION") &&
    normalized.includes("EQUIPOS") &&
    normalized.includes("MAQUINARIA")
  ) {
    return "machineryInvestment";
  }

  const matched = Object.values(INSURANCE_MATERIAL_RULES).find((rule) => {
    const ruleLabel = normalizeInsuranceMaterialText(rule.label);
    return normalized.includes(ruleLabel) || ruleLabel.includes(normalized);
  });

  return matched?.value || DEFAULT_INSURANCE_MATERIAL_TYPE;
}

export function toInsuranceNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const raw = String(value ?? "").trim();

  if (!raw) return 0;

  const normalized =
    raw.includes(",") && !raw.includes(".")
      ? raw.replace(",", ".")
      : raw.replace(/,/g, "");

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
}

export function normalizeInsuranceCurrency(value) {
  const normalized = String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

  if (
    normalized === "EUR" ||
    normalized.includes("EURO") ||
    normalized.includes("€")
  ) {
    return "EUR";
  }

  if (
    normalized === "USD" ||
    normalized.includes("DOLAR") ||
    normalized.includes("$US") ||
    normalized.includes("US$")
  ) {
    return "USD";
  }

  return normalized || "USD";
}

export function getInsuranceExchangeRate(currency, exchangeRateEurUsd) {
  const normalizedCurrency = normalizeInsuranceCurrency(currency);

  if (normalizedCurrency === "EUR") {
    return toInsuranceNumber(exchangeRateEurUsd);
  }

  return 1;
}

export function getInvoiceItemTotal(extra) {
  const hasItemTotal =
    extra?.itemTotal !== null &&
    extra?.itemTotal !== undefined &&
    extra?.itemTotal !== "";
  const itemTotal = toInsuranceNumber(extra?.itemTotal);

  if (hasItemTotal) {
    return itemTotal;
  }

  const amount = toInsuranceNumber(extra?.amount);
  const unitPrice = toInsuranceNumber(extra?.unitPrice);
  const discount = toInsuranceNumber(extra?.discount);

  return amount * unitPrice - discount;
}

export function calculateInsuredItemTotal(extra, options = {}) {
  const rule = getInsuranceRule(extra?.insuranceMaterialType);
  const invoiceTotal = getInvoiceItemTotal(extra);

  const currency = normalizeInsuranceCurrency(
    options.currency || extra?.purchaseOrderCurrency || "USD",
  );

  const exchangeRate = getInsuranceExchangeRate(
    currency,
    options.exchangeRateEurUsd,
  );

  const invoiceTotalUsd = invoiceTotal * exchangeRate;
  const insuredTotalUsd = invoiceTotalUsd * rule.factor;

  return {
    rule,
    currency,
    exchangeRate,
    invoiceTotal,
    invoiceTotalUsd,
    insuredTotal: insuredTotalUsd,
    insuredTotalUsd,
  };
}
